import { MirrorSelect } from '@crasman/multiselect';
//import { MirrorSelect } from '../../multiselect_symlink/src';
import Mustache from 'mustache';

const MULTISELECT_CLASSNAME_PREFIX = 'r3multiselect__';

const minMaxPairGTHandler = eventTarget => {
  const target = eventTarget.target || eventTarget;
  const multiselectObject = window.r3multiselects[target.id];
  const cutOff = parseFloat(target.value);
  [...multiselectObject.minMaxPair.element.options].forEach(option => {
    const disabled = parseFloat(option.value) >= cutOff;
    option.toggleAttribute('disabled', disabled);
    if (disabled && option.selected) {
      option.removeAttribute('selected');
      option.selected = false;
    }
  });
};

const minMaxPairLTHandler = eventTarget => {
  const target = eventTarget.target || eventTarget;
  const multiselectObject = window.r3multiselects[target.id];
  const cutOff = parseFloat(target.value);
  [...multiselectObject.minMaxPair.element.options].forEach(option => {
    const disabled = parseFloat(option.value) <= cutOff;
    option.toggleAttribute('disabled', disabled);
    if (disabled && option.selected) {
      option.removeAttribute('selected');
      option.selected = false;
    }
  });
};

class MultiselectLegacyWrapper {
  constructor(element) {
    this.element = element;

    const datasets = [];
    for (const [key, value] of Object.entries(Object.assign({}, this.element.dataset))) {
      datasets.push({ datasetName: key, datasetValue: value });
    }

    this.selectDetails = {
      name: this.element.name,
      form: this.element.form,
      isDisabled: this.element.disabled,
      isMultiple: this.element.type === 'select-multiple', // If select is multiple
      style: this.element.style.cssText,
      datasets: datasets,
      defaultCaption: this.element.dataset.caption,
      classes: this.element.className.replace('remultiselect', ''),
      latestSelection: {
        wasUnChecked: false, // Defines if last selection was unselect
        value: null, // Last selected / unselected value
      },
    };
  }

  /**
   * @description Adds callback methods to specific events in the Multiselect lifecycle
   * @param {{onValueChange:method}} callbacks - object with a member onValueChange. Earlier version of the multiselect wrapper had other methods too, but they aren't being used in this project, so they will not be supported.
   * @memberof MultiselectLegacyWrapper
   */
  addCallback(callbacks) {
    this.callbacks = callbacks;
    if (typeof callbacks.onValueChange === 'function') this.element.addEventListener('change', () => callbacks.onValueChange(this));
  }

  /**
   * @description Adds options with matching values to given array
   * @param {{value:string,shownValue:string}[] |
   * {label:string,options:{value:string,parent_value:string,selected:boolean}[]}[]} newOptions
   * - optgroups = false: An array of objects with value and shownValue properties.
   * - optgroups = true: An array of objects with label (optgroup label) and options properties (array of objects with value, parent_value and selected properties)
   * @param {boolean} optgroups - whether or not the options are optgroups
   * @memberof MultiselectLegacyWrapper
   */
  addOptions(newOptions, optgroups = false) {
    if (optgroups) {
      newOptions.forEach(opt => {
        const optgroup = document.createElement('optgroup');
        optgroup.setAttribute('label', opt.label);
        opt.options.forEach(o => {
          const option = document.createElement('option');
          option.setAttribute('value', o.value);
          option.innerText = o.value;
          option.selected = o.selected;
          optgroup.append(option);
        });
        this.element.append(optgroup);
      });
    } else {
      newOptions.forEach(opt => {
        const option = document.createElement('option');
        option.setAttribute('value', opt.value);
        option.innerText = opt.shownValue;
        this.element.append(option);
      });
    }
  }

  /**
   * @description Removes options with matching values to given array
   * @param {string[]} removeOptions - in the format [ 'value1', 'value2' ]
   * @memberof MultiselectLegacyWrapper
   */
  removeOptions(removeOptions) {
    this.options.map(o => {
      if (removeOptions.includes(o.value)) {
        o.element.remove();
      }
    });
  }

  removeAllOptgroups() {
    while (this.element.firstChild) {
      this.element.removeChild(this.element.firstChild);
    }
  }

  /**
   * @description deselects all options of the elemenet
   * @param {boolean} runCallBack - wether or not to call callback (if one has been provided).
   * @memberof MultiselectLegacyWrapper
   */
  unSelectAll(runCallBack) {
    const container = this.element.closest(`.${MULTISELECT_CLASSNAME_PREFIX}container`);

    container.querySelectorAll('select option:checked')?.forEach(o => {
      o.removeAttribute('selected');
      o.selected = false;
    });

    container.querySelectorAll(`.${MULTISELECT_CLASSNAME_PREFIX}option.selected`)?.forEach(o => {
      o.classList.remove('selected');
      o.removeAttribute('aria-selected');
    });
    container.multiSelectObject.selectElement.selectedIndex = -1;

    // Broadcast that something changed
    container.multiSelectObject.selectElement.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Doesn't seem to be in use, so nothing to do...
  openSelect() {
    this.#legacyWarning('openSelect');
  }

  // Doesn't seem to be in use, so nothing to do...
  closeSelect() {
    this.#legacyWarning('closeSelect');
  }

  // Doesn't seem to be doing anything useful anyway, so nothing to do...
  clearSearchInput() {
    this.#legacyWarning('clearSearchInput');
  }

  /**
   * @description Indicates wether any options on the select have been selected
   * @returns {boolean}
   * @memberof MultiselectLegacyWrapper
   */
  anyOptionsSelected() {
    return this.element.selectedIndex > 0;
  }

  /**
   * @description Gets the value or values of the multiselect
   * @returns {string|string[]}
   * @memberof MultiselectLegacyWrapper
   */
  getValue() {
    return this.element.type === 'select-multiple' ? this.value : this.value[0] || '';
  }

  /**
   * @description Toggles the multiselect elemenet off
   * @memberof MultiselectLegacyWrapper
   */
  disableMultiselect() {
    this.element.toggleAttribute('disabled', true);
  }

  /**
   * @description Toggles the multiselect elemenet on
   * @memberof MultiselectLegacyWrapper
   */
  enableMultiselect() {
    this.element.toggleAttribute('disabled', false);
  }

  /**
   * @description Pairs a min/max select options pair so that each limit which options can be selected from eachother, so that the min/max ranges don't overlap illogically
   * @memberof MultiselectLegacyWrapper
   * @returns {undefined}
   */
  handleMinMaxSelectors() {
    const { lessThan, greaterThan } = this.element.dataset;
    const pair = lessThan || greaterThan;
    if (!pair) return false;
    const greaterThanPair = greaterThan ? this : window.r3multiselects[pair];
    const lessThanPair = lessThan ? this : window.r3multiselects[pair];

    greaterThanPair.minMaxPair = lessThanPair;
    lessThanPair.minMaxPair = greaterThanPair;

    greaterThanPair.element.addEventListener('change', minMaxPairGTHandler);
    minMaxPairGTHandler(greaterThanPair.element); // Call once to initialize on load

    lessThanPair.element.addEventListener('change', minMaxPairLTHandler);
    minMaxPairLTHandler(lessThanPair.element); // Call once to initialize on load
  }

  /**
   * @typedef OptionsCollection
   * @property {string} value
   * @property {string} shownValue
   * @property {Node} element
   * @property {HTMLElement.dataset} dataset
   */

  /**
   * @description Getter of the select element's options
   * @returns {OptionsCollection[]} an array of objects based on the selects options
   * @memberof MultiselectLegacyWrapper
   */
  get options() {
    return [...this.element.options].map(o => ({
      value: o.value,
      shownValue: o.innerText,
      element: o,
      dataset: o.dataset,
    }));
  }

  /**
   * @description
   * @returns {string[]}
   * @memberof MultiselectLegacyWrapper
   */
  get value() {
    return this.options.filter(o => o.element.selected).map(o => o.value);
  }

  /**
   * @description
   * @param {string | string[]} targetValue - value of the option to choose
   * @memberof MultiselectLegacyWrapper
   */
  set value(targetValue) {
    if (Array.isArray(targetValue)) {
      const unselectOptions = [...this.element.querySelectorAll('option:checked')].filter(o => !targetValue.includes(o.value));
      let hasChanged = unselectOptions.length > 0;
      unselectOptions.forEach(o => (o.selected = false));
      targetValue.forEach(v => {
        const option = this.element.querySelector(`option[value="${v}"]:not(:checked)`);
        if (option) {
          hasChanged = true;
          option.selected = true;
        }
      });
      if (hasChanged) this.element.dispatchEvent(new CustomEvent('change', { detail: { dispatcher: 'MultiselectLegacyWrapper' } }));
    } else {
      const matchValue = this.options.findIndex(o => o.value === targetValue);
      if (matchValue > -1) {
        this.element.selectedIndex = matchValue;
        this.element.dispatchEvent(new CustomEvent('change', { detail: { dispatcher: 'MultiselectLegacyWrapper' } }));
      } else {
        console.info(
          `MultiselectLegacyWrapper tried setting value to ${
            this.element.id
          }. Given option not found. Got [${typeof targetValue} ${targetValue}], expected string, one of [${this.options.map(
            o => o.value,
          )}]`,
        );
      }
    }
  }

  /**
   * @description logs a warning in the console not to use this feature, since no support for it will be provided.
   * @private
   * @param {string} feature - name of the called unsupported feature
   * @returns {undefined}
   * @memberof MultiselectLegacyWrapper
   */
  #legacyWarning(feature) {
    console.warn(`Multiselect-builder says you shouldn't be using ${feature || 'this'}...`);
  }
}

class MirrorSelectLauncher {
  /**
   * @private
   * @property {object} legacyElements - collection of prototype DOM nodes, clones of which are be used in constructing multiselect UI elements on the page
   */
  #elements = {};

  /**
   * @private
   * @property {object} settings - multiselect initialization configuration template object.
   */
  settings = {
    listElement: { size: 12, preventScrollOnFocus: false },
    baseClassName: MULTISELECT_CLASSNAME_PREFIX,
    buttonElement: {},
    mirrorNative: true,
    cloneAttributesWhitelist: ['style'],
    setCssProperties: false,
  };

  constructor() {
    if (!window.r3multiselects || typeof window.r3multiselects !== 'object') window.r3multiselects = {}; // Yeah this is horrible, but legacy requires it...
    this.initButtonElements();
    this.initSelect = this.initSelect.bind(this);
    //this.updateClearCount = this.updateClearCount.bind(this);
    this.init();
  }

  /**
   * @description class initializer
   * @returns {undefined}
   * @memberof MirrorSelectLauncher
   */
  init() {
    const nativeSelects = document.querySelectorAll('.r3multiselect');
    nativeSelects.forEach(this.initSelect);
    nativeSelects.forEach(select => {
      window.r3multiselects[select.id].handleMinMaxSelectors();
    });
    this.bindButtonListeners(document.documentElement);
  }

  /**
   * @description MirrorSelect instance initializer
   * @param {HTMLSelectElement} select - Select DOM node
   * @returns {undefined}
   * @memberof MirrorSelectLauncher
   */
  initSelect(select) {
    const currentSettings = { ...this.settings };
    const isRequired = select.required;
    if (select.dataset?.caption) {
      currentSettings.buttonElement.textContent = select.dataset.caption;
      currentSettings.buttonElement.showPlaceholder = true;
    } else {
      currentSettings.buttonElement.showPlaceholder = false;
    }

    const searchableOptions = {
      searchable: true,
      selectionClears: true,
      enterSelects: true,
    };
    if (select.dataset?.searchable) currentSettings.searchable = searchableOptions;

    const multiSelectObject = new MirrorSelect(select, currentSettings);
    multiSelectObject.containerElement.multiSelectObject = multiSelectObject; // Just FYI this is circular referencing.  multiSelectObject.containerElement contains a referece to multiSelectObject itself. It's there so DOM elemenets and their events can access it...
    if (select.dataset?.wrapperClass) {
      multiSelectObject.containerElement.classList.add(...select.dataset.wrapperClass.split(' '));
    }

    // These are done mostly just to adapt to previous MultiSelect behaviour that expects these...
    multiSelectObject.optionsListElement.setAttribute('data-select-name', select.name);
    multiSelectObject.containerElement.classList.add('select-container');
    multiSelectObject.containerElement.classList.add(...select.classList);
    multiSelectObject.buttonElement.classList.add(...select.classList);

    if (select.type === 'select-multiple') {
      multiSelectObject.containerElement.classList.add(`${MULTISELECT_CLASSNAME_PREFIX}container--multiple`);
    }

    // Adding two clunky buttons because previous Select had them too...
    this.initButtons(select, multiSelectObject);
    if (isRequired) multiSelectObject.selectElement.toggleAttribute('required', isRequired);
    window.r3multiselects[select.id] = new MultiselectLegacyWrapper(select);
  }

  /**
   * @description Places "close" and "clear" buttons on selects, if needed.
   * @param {HTMLSelectElement} select - A native select DOM node
   * @param {MirrorSelect} multiSelectObject - MultiSelect object instance.
   * @returns {undefined}
   * @memberof MirrorSelectLauncher
   */
  initButtons(select, multiSelectObject) {
    const { clearButton } = this.getButtonElements();
    if (clearButton && !select.dataset.noClear) {
      this.placeButton(clearButton, multiSelectObject, 'top');
    } else {
      clearButton.remove(); // Your services are no longer required :(
    }

    // Old code, copied from elsewhere. Not needed for now, but might turn out useful later, so saving just in case
    // if (select.type === 'select-multiple') {
    //   this.placeButton(closeButton, multiSelectObject, 'bottom');
    // } else {
    //   closeButton.remove(); // Your services are no longer required :(
    // }
  }

  /**
   * @private
   * @property {object} buttonEventTypes - list of event listener types to listen for, when binding buttons.
   */
  #buttonEventTypes = ['click', 'keydown', 'mousedown'];

  /**
   * @description Places "close" and "clear" button event listeners on whole document.
   * @param {HTMLElement} element - A DOM node to which the evenet listeners should be bound to. This might as well be the whole document, so we only attach one listener for each event and handler type, and all the select buttons on the page can use the same handlers.
   * @returns {undefined}
   * @memberof MirrorSelectLauncher
   */
  bindButtonListeners(element) {
    this.#buttonEventTypes.forEach(type => {
      element.addEventListener(type, this.closeButtonHandler);
    });
    this.#buttonEventTypes.forEach(type => {
      element.addEventListener(type, this.clearButtonHandler);
    });
  }

  /**
   * @description General event handler for clear button interactions
   * @param {Event} event - A DOM event, presumably click or keypress
   * @returns {undefined}
   * @memberof MirrorSelectLauncher
   */
  clearButtonHandler = event => {
    // Was this event was addressed to me?
    const controlClick = event.target.closest(`.${MULTISELECT_CLASSNAME_PREFIX}control-container--clear`);

    if (!controlClick) return; // It was not.

    // We're handling this, so we don't want this to bubble up
    event.preventDefault();
    event.stopImmediatePropagation();

    const container = controlClick.closest(`.${MULTISELECT_CLASSNAME_PREFIX}container`);

    container.querySelectorAll('select option')?.forEach(o => {
      o.removeAttribute('selected');
      o.selected = false;
    });
    container.multiSelectObject.selectElement.selectedIndex = -1;

    container.multiSelectObject.closeOptionsList();
    // Broadcast that something changed
    container.multiSelectObject.selectElement.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * @description General event handler for close button interactions
   * @param {Event} event - A DOM event, presumably click or keypress
   * @returns {undefined}
   * @memberof MirrorSelectLauncher
   */
  closeButtonHandler = event => {
    // Was this event was addressed to me?
    const controlClick = event.target.closest(`.${MULTISELECT_CLASSNAME_PREFIX}control-container--close`);
    if (!controlClick) return; // It was not.

    // We're handling this, so we don't want this to bubble up
    event.preventDefault();
    event.stopImmediatePropagation();

    const container = controlClick.closest(`.${MULTISELECT_CLASSNAME_PREFIX}container`);
    container.multiSelectObject.closeOptionsList();
  };

  /**
   * @description General event handler for clear button interactions
   * @param {Node} button - A DOM node
   * @param {MirrorSelect} multiSelectObject - an instance of the MultiSelect class for a specific select element
   * @param {string} placement - 'bottom' for button to be placed at the bottom, defaults to top of the list.
   * @returns {undefined}
   * @memberof MirrorSelectLauncher
   */
  placeButton(button, multiSelectObject, placement = null) {
    if (placement === 'bottom') {
      // Button goes after the list
      multiSelectObject.optionsListElement.parentNode.append(button);
    } else {
      // Button goes before the list
      multiSelectObject.optionsListElement.parentNode.insertBefore(button, multiSelectObject.optionsListElement);
    }
  }

  /**
   * @description Clones and returns two button elements
   * @returns {{closeButton:Node, clearButton:Node}}
   * @memberof MirrorSelectLauncher
   */
  getButtonElements() {
    return {
      clearButton: this.#elements.data.clearButton.cloneNode(true),
    };
  }

  /**
   * @description Initialises clear button element. This could be cleaned up considerably...
   * @memberof MirrorSelectLauncher
   */
  initButtonElements() {
    const tmplClearButton = document.querySelector('#mustache-select-clear-selection')?.innerHTML;

    const clearButton = document.createElement('div');
    // Render legacy content
    clearButton.innerHTML = Mustache.render(tmplClearButton);

    clearButton.classList.add(`${MULTISELECT_CLASSNAME_PREFIX}control-container`);
    clearButton.classList.add(`${MULTISELECT_CLASSNAME_PREFIX}control-container--clear`);

    // This is no longer needed
    clearButton.remove();

    // This progress needs to be done just once
    this.#elements.data = { clearButton };
  }
}

export const init = () => new MirrorSelectLauncher();
